<script setup lang="ts">
import { nextTick, h } from 'vue'
import {
  User,
  LogOut,
} from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { ArrowDown } from '@element-plus/icons-vue'

const props = defineProps<{
  user: {
    name: string
    email: string
    avatar: string
  }
  collapsed?: boolean
}>()

const router = useRouter()
const userStore = useUserStore()

const handleLogout = async () => {
  // 先保存需要的信息
  const employeeId = userStore.employee?.id
  const isCustomerService = (userStore.employee as any)?.isCustomerService
  
  // 如果是客服账号，后台异步设置为离线（不阻塞退出）
  if (isCustomerService && employeeId) {
    Promise.resolve().then(async () => {
      try {
        const { setOnlineStatus } = await import('@/api/customerServiceSeat')
        await setOnlineStatus(employeeId, false)
        console.log('[退出登录] 客服账号已自动设置为离线')
      } catch (error: any) {
        console.warn('[退出登录] 设置客服离线状态失败:', error.message)
      }
    })
  }
  
  // 清除用户信息和 token
  userStore.clearUser()
  
  // 等待下一个 tick，确保状态已清除
  await nextTick()
  
  // 使用 router 跳转到登录页（会自动处理 base path）
  // 使用 replace 而不是 push，避免在历史记录中留下记录
  router.replace('/login').catch(() => {
    // 如果路由跳转失败，使用 window.location（考虑 base path）
    window.location.href = '/admin/login'
  })
}
</script>

<template>
  <el-dropdown trigger="click" placement="top-start" @command="(cmd: string) => cmd === 'logout' ? handleLogout() : router.push(cmd)">
    <div class="flex items-center gap-2 cursor-pointer p-2 rounded-lg transition-colors" style="background-color: transparent;">
      <el-avatar :size="32" :src="user.avatar" class="rounded-lg">
        {{ user.name ? user.name.charAt(0).toUpperCase() : 'U' }}
      </el-avatar>
      <div v-if="!collapsed" class="flex-1 text-left min-w-0">
        <div class="text-sm font-semibold truncate">{{ user.name }}</div>
        <div class="text-xs opacity-70 truncate">{{ user.email }}</div>
      </div>
      <el-icon v-if="!collapsed">
        <ArrowDown />
      </el-icon>
    </div>
    <template #dropdown>
      <el-dropdown-menu>
        <el-dropdown-item command="/dashboard/profile">
          <el-icon>
            <component :is="h(User)" />
          </el-icon>
          <span class="ml-2">个人资料</span>
        </el-dropdown-item>
        <el-dropdown-item divided command="logout">
          <el-icon>
            <component :is="h(LogOut)" />
          </el-icon>
          <span class="ml-2">退出登录</span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </template>
  </el-dropdown>
</template>
